// src/contexts/fiscal/TransporteFiscalContext.js
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';
import { inativarCadastroFiscal, listarCadastrosFiscais, salvarCadastroFiscal } from '../../services/fiscal/TransporteFiscalService';
import { useAlert } from '../geral/AlertContext';
import VeiculoCad from '../../pages/fiscal/mdfe/VeiculoCad';
import CondutorCad from '../../pages/fiscal/mdfe/CondutorCad';
import SeguradoraCad from '../../pages/fiscal/mdfe/SeguradoraCad';
import VeiculoSearch from '../../pages/fiscal/mdfe/VeiculoSearch';
import CondutorSearch from '../../pages/fiscal/mdfe/CondutorSearch';
import SeguradoraSearch from '../../pages/fiscal/mdfe/SeguradoraSearch';
import { Veiculo } from '../../models/fiscal/Veiculo';
import { Condutor } from '../../models/fiscal/Condutor';
import { Seguradora } from '../../models/fiscal/Seguradora';

const TransporteFiscalContext = createContext();
export const useTransporteFiscal = () => useContext(TransporteFiscalContext);

export const TransporteFiscalProvider = ({ children }) => {
  const [listas, setListas] = useState({ veiculos: [], condutores: [], seguradoras: [] });
  const [loading, setLoading] = useState(false);
  const [modalCad, setModalCad] = useState({ tipo: null, initialRegistro: null });
  const [modalSearch, setModalSearch] = useState(null);
  const cadResolverRef = useRef(null);
  const searchResolverRef = useRef(null);
  const { showAlert } = useAlert();

  const carregar = useCallback(async (tipo, filialId) => {
    if (!filialId) return [];
    setLoading(true);
    try {
      const result = await listarCadastrosFiscais(tipo, filialId);
      setListas((prev) => ({ ...prev, [tipo]: result || [] }));
      return result || [];
    } catch (error) {
      showAlert(error.message || 'Erro ao carregar cadastro fiscal.', 'danger');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  const salvar = useCallback(async (tipo, item) => {
    setLoading(true);
    try {
      const result = await salvarCadastroFiscal(tipo, item);
      setListas((prev) => ({
        ...prev,
        [tipo]: item.id
          ? prev[tipo].map((registro) => (registro.id === result.id ? result : registro))
          : [...prev[tipo], result],
      }));
      showAlert('Cadastro salvo com sucesso!');
      return result;
    } catch (error) {
      showAlert(error.message || 'Erro ao salvar cadastro fiscal.', 'danger');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  const inativar = useCallback(async (tipo, id) => {
    setLoading(true);
    try {
      await inativarCadastroFiscal(tipo, id);
      setListas((prev) => ({
        ...prev,
        [tipo]: prev[tipo].map((registro) => (registro.id === id ? { ...registro, ativo: false } : registro)),
      }));
      showAlert('Cadastro excluído com sucesso!');
    } catch (error) {
      showAlert(error.message || 'Erro ao inativar cadastro fiscal.', 'danger');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  const abrirCadastro = useCallback((tipo, initialRegistro) => new Promise((resolve) => {
    setModalCad({ tipo, initialRegistro });
    cadResolverRef.current = resolve;
  }), []);

  const fecharCadastro = useCallback((registro = null) => {
    setModalCad({ tipo: null, initialRegistro: null });
    cadResolverRef.current?.(registro);
    cadResolverRef.current = null;
  }, []);

  const abrirPesquisa = useCallback((tipo) => new Promise((resolve) => {
    carregar(tipo, 1);
    setModalSearch(tipo);
    searchResolverRef.current = resolve;
  }), [carregar]);

  const fecharPesquisa = useCallback((registro = null) => {
    setModalSearch(null);
    searchResolverRef.current?.(registro);
    searchResolverRef.current = null;
  }, []);

  const showVeiculoCad = useCallback((registro = new Veiculo()) => abrirCadastro('veiculos', registro), [abrirCadastro]);
  const showCondutorCad = useCallback((registro = new Condutor()) => abrirCadastro('condutores', registro), [abrirCadastro]);
  const showSeguradoraCad = useCallback((registro = new Seguradora()) => abrirCadastro('seguradoras', registro), [abrirCadastro]);
  const showVeiculoSearch = useCallback(() => abrirPesquisa('veiculos'), [abrirPesquisa]);
  const showCondutorSearch = useCallback(() => abrirPesquisa('condutores'), [abrirPesquisa]);
  const showSeguradoraSearch = useCallback(() => abrirPesquisa('seguradoras'), [abrirPesquisa]);

  const value = useMemo(() => ({
    ...listas, loading, carregar, salvar, inativar,
    showVeiculoCad, showCondutorCad, showSeguradoraCad,
    showVeiculoSearch, showCondutorSearch, showSeguradoraSearch,
  }), [listas, loading, carregar, salvar, inativar, showVeiculoCad, showCondutorCad,
    showSeguradoraCad, showVeiculoSearch, showCondutorSearch, showSeguradoraSearch]);

  return <TransporteFiscalContext.Provider value={value}>
    {children}
    <VeiculoSearch show={modalSearch === 'veiculos'} onHide={() => fecharPesquisa()} onSelectVeiculo={fecharPesquisa} registros={listas.veiculos} />
    <CondutorSearch show={modalSearch === 'condutores'} onHide={() => fecharPesquisa()} onSelectCondutor={fecharPesquisa} registros={listas.condutores} />
    <SeguradoraSearch show={modalSearch === 'seguradoras'} onHide={() => fecharPesquisa()} onSelectSeguradora={fecharPesquisa} registros={listas.seguradoras} />
    <VeiculoCad show={modalCad.tipo === 'veiculos'} onHide={() => fecharCadastro()} initialRegistro={modalCad.initialRegistro} onSaveRegistro={fecharCadastro} />
    <CondutorCad show={modalCad.tipo === 'condutores'} onHide={() => fecharCadastro()} initialRegistro={modalCad.initialRegistro} onSaveRegistro={fecharCadastro} />
    <SeguradoraCad show={modalCad.tipo === 'seguradoras'} onHide={() => fecharCadastro()} initialRegistro={modalCad.initialRegistro} onSaveRegistro={fecharCadastro} />
  </TransporteFiscalContext.Provider>;
};
